import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { weatherApi } from '../services/api';
import { WeatherDay } from '../types';
import '../styles/WeatherSection.css';

interface WeatherIconConfig {
  icon: string;
  color: string;
}

const getWeatherIconConfig = (day: WeatherDay): WeatherIconConfig => {
  const hasHeavyRain   = day.precipitation > 20;
  const hasRain        = day.precipitation > 5 || day.precipProbability > 60;
  const hasPartlyRain  = day.precipProbability > 30;
  const isHot          = day.tempMax > 30;

  if (hasHeavyRain)  return { icon: 'bi-cloud-rain-heavy-fill', color: '#60a5fa' };
  if (hasRain)       return { icon: 'bi-cloud-rain-fill',        color: '#7dd3fc' };
  if (hasPartlyRain) return { icon: 'bi-cloud-drizzle-fill',     color: '#93c5fd' };
  if (isHot)         return { icon: 'bi-sun-fill',               color: '#fbbf24' };
  if (day.tempMax < 15) return { icon: 'bi-cloud-fill',          color: '#cbd5e1' };
  return               { icon: 'bi-cloud-sun-fill',              color: '#fcd34d' };
};

const formatDate = (dateString: string) => {
  const date  = new Date(dateString);
  const today = new Date();

  const shortDate = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  if (date.toDateString() === today.toDateString()) {
    return { label: 'Hoje', date: shortDate };
  }

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toDateString() === tomorrow.toDateString()) {
    return { label: 'Amanhã', date: shortDate };
  }

  const weekday = date.toLocaleDateString('pt-BR', { weekday: 'long' });
  const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return { label: capitalized, date: shortDate };
};

const WeatherSection: React.FC = () => {
  const [weather, setWeather] = useState<WeatherDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const data = await weatherApi.getCurrentWeather();
        setWeather(data.slice(0, 7));
      } catch (error) {
        console.error('Error loading weather:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, []);

  return (
    <section className="weather-section">
      <div
        className="weather-bg-parallax"
        style={{ backgroundImage: "url('/img/bg-tempo.jpg')" }}
      />
      <div className="weather-overlay" />

      <Container className="weather-container">
        <div className="weather-header">
          <div className="section-header">
            <span className="section-badge section-badge--light">Bagé, RS</span>
            <h2 className="section-title" style={{ color: 'var(--color-white)' }}>
              Previsão do Tempo
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            <div className="weather-grid">
              {weather.map((day, index) => {
                const { icon, color } = getWeatherIconConfig(day);
                const { label, date } = formatDate(day.date);

                return (
                  <div key={index} className="weather-card">
                    <div className="weather-date">
                      <span className="weather-weekday">{label}</span>
                      <span className="weather-day-date">{date}</span>
                    </div>

                    <div className="weather-card-row">
                      <div className="weather-icon-wrapper">
                        <i className={`bi ${icon} weather-icon`} style={{ color }}></i>
                      </div>
                      <div className="weather-card-temps">
                        <div className="weather-temp">{Math.round(day.tempMax)}°</div>
                        <div className="weather-temp-range">
                          {Math.round(day.tempMin)}° / {Math.round(day.tempMax)}°
                        </div>
                      </div>
                    </div>

                    <div className="weather-details">
                      <div className="weather-detail">
                        <i className="bi bi-droplet-fill" style={{ color: '#7dd3fc' }}></i>
                        <span>{Math.round(day.precipitation)}mm</span>
                      </div>
                      <div className="weather-detail">
                        <i className="bi bi-wind" style={{ color: '#a5f3fc' }}></i>
                        <span>{Math.round(day.windSpeed)}km/h</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="weather-alert">
              <a
                href="https://portal.inmet.gov.br/"
                target="_blank"
                rel="noopener noreferrer"
                className="weather-alert-link"
              >
                <i className="bi bi-exclamation-triangle"></i>
                Ver alertas INMET
              </a>
            </div>
          </>
        )}
      </Container>
    </section>
  );
};

export default WeatherSection;
